import { parse, stringify } from 'hub-http/helpers/params';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { generatePath } from './urls';
import debounce from 'react-utils/debounce';
import { UsageTracker } from './tracking';
import { ScreenType } from '../types';
export const useQueryParams = () => {
  const {
    search
  } = useLocation();
  return useMemo(() => search.length ? parse(search.substring(1, search.length)) : {}, [search]);
};
export const useMediaQuery = mediaQuery => {
  const [isMatching, setIsMatching] = useState(!!window.matchMedia(mediaQuery).matches);
  useEffect(() => {
    const mql = window.matchMedia(mediaQuery);

    const changeHandler = e => setIsMatching(!!e.matches);

    mql.addEventListener('change', changeHandler);
    return () => {
      mql.removeEventListener('change', changeHandler);
    };
  }, [mediaQuery]);
  return isMatching;
};
export const useQueryParam = paramName => {
  const queryParams = useQueryParams();
  const history = useHistory();
  const setParam = useCallback(value => {
    const search = window.location.search;
    const currentParams = search.length ? parse(search.substring(1, search.length)) : {};
    history.replace(Object.assign({}, history.location, {
      search: stringify(Object.assign({}, currentParams, {
        [paramName]: value
      }))
    }));
  }, [history, paramName]);
  return [queryParams[paramName], setParam];
};
export const useDebounce = (callback, cooldown = 500) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => debounce(callback, cooldown), [cooldown]);
};
export const useDebouncedValue = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return debouncedValue;
};
export const useFilter = filterQueryName => {
  const [selectedParams, setSelectedParams] = useQueryParam(filterQueryName);
  const selectedItems = Array.isArray(selectedParams) ? selectedParams : selectedParams !== undefined ? [selectedParams] : [];

  const handleToggle = filterValue => {
    if (!selectedItems.includes(filterValue)) {
      setSelectedParams([...selectedItems, filterValue]);
    } else {
      setSelectedParams(selectedItems.filter(item => item !== filterValue));
    }
  };

  const handleClear = () => {
    setSelectedParams();
  };

  const handleReplace = filterValue => {
    setSelectedParams(filterValue);
  };

  return {
    selectedItems,
    handleToggle,
    handleClear,
    handleReplace
  };
};
export const useJSONFilter = filterQueryName => {
  const {
    selectedItems,
    handleToggle: toggle,
    handleReplace: replace,
    handleClear
  } = useFilter(filterQueryName);
  const selectedObjects = useMemo(() => selectedItems.map(obj => {
    try {
      return JSON.parse(obj);
    } catch (e) {
      if (e instanceof SyntaxError) {
        return null;
      }

      throw e;
    }
  }).filter(Boolean), [selectedItems]);

  const handleToggle = filterValue => toggle(JSON.stringify(filterValue));

  const handleReplace = filterValue => replace(JSON.stringify(filterValue));

  return {
    selectedObjects,
    handleToggle,
    handleClear,
    handleReplace
  };
};
export const useNavigate = () => {
  const history = useHistory();
  const navigate = useCallback(path => history.push(path), [history]);
  const navigateToUser = useCallback(memberId => {
    navigate(generatePath.user(memberId));
  }, [navigate]);
  const navigateToEdit = useCallback(() => {
    navigate(generatePath.editUser());
  }, [navigate]);
  const navigateToDirectory = useCallback(() => {
    navigate(generatePath.directory());
  }, [navigate]);
  return {
    navigate,
    navigateToUser,
    navigateToEdit,
    navigateToDirectory
  };
};
export const useIsInView = ref => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  useEffect(() => {
    if (!ref.current) return () => {};
    const observer = new IntersectionObserver(([entry]) => setIsIntersecting(entry.isIntersecting));
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [ref]);
  return isIntersecting;
};

const matchRoute = path => {
  if (path === '/') {
    return ScreenType.Directory;
  } else if (path === '/edit') {
    return ScreenType.Edit;
  } else if (path.startsWith('/member/')) {
    return ScreenType.Member;
  }

  return null;
};

export const useTracking = () => {
  const {
    pathname,
    search
  } = useLocation();
  useEffect(() => {
    const matchedRoute = matchRoute(pathname);

    if (matchedRoute) {
      UsageTracker.trackPageView(matchedRoute, {
        pathname,
        search
      });
    } else {
      // eslint-disable-next-line no-console
      console.error('No route matched for', pathname);
    }
  }, [pathname, search]);
};
export const useScript = ({
  id,
  src,
  async = true
}) => {
  useEffect(() => {
    const scriptEl = document.createElement('script');
    scriptEl.id = id;
    scriptEl.src = src;
    scriptEl.async = async;
    document.body.appendChild(scriptEl);
    return () => {
      document.body.removeChild(scriptEl);
    };
  }, [id, src, async]);
};